import { useEffect, useState } from "react";
import { CheckCircle2, MessageCircleQuestion, Ban, FileText, Pencil, UploadCloud, ExternalLink, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { paymentMethodLabel } from "@/constants/outsidePurchases";
import { qboTransactionLink } from "@/lib/qbo";
import { fmtDate, fmtDateTime } from "@/lib/utils";
import SearchableSelect from "@/components/SearchableSelect";
import { DocPreviewBody } from "@/components/DocumentsPanel";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

function DocPreview({ doc }) {
  return (
    <div className="max-h-72 overflow-auto rounded-lg border border-slate-200 bg-slate-50">
      <DocPreviewBody doc={doc} height="h-72" compact />
    </div>
  );
}

export default function AccountingReviewDrawer({ item, open, onOpenChange, onChanged, isQboMode = false, startEditing = false }) {
  const [detail, setDetail] = useState(null);
  const [costCodes, setCostCodes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [showNeedsInfo, setShowNeedsInfo] = useState(false);
  const [needsInfoMessage, setNeedsInfoMessage] = useState("");
  const [showExclude, setShowExclude] = useState(false);
  const [excludeReason, setExcludeReason] = useState("");
  const [editingMapping, setEditingMapping] = useState(false);
  const [memo, setMemo] = useState("");
  const [lineEdits, setLineEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [qboEnvironment, setQboEnvironment] = useState(null);
  const [qboAccounts, setQboAccounts] = useState([]);
  const [paymentAccount, setPaymentAccount] = useState(null); // null = unchanged

  const load = () => {
    if (!item) return;
    api.get(`/contractor/accounting/${item.type}/${item.id}`).then((r) => {
      setDetail(r.data);
      setMemo(r.data.transaction.accounting_memo || "");
    });
  };

  useEffect(() => {
    if (!open || !item) return;
    setDetail(null);
    setShowNeedsInfo(false); setNeedsInfoMessage("");
    setShowExclude(false); setExcludeReason("");
    // Opened from Sync Issues via "Fix cost codes" → land straight in the editor.
    setEditingMapping(startEditing); setLineEdits({}); setPaymentAccount(null);
    load();
    api.get("/contractor/cost-codes", { params: { include_inactive: true } }).then((r) => setCostCodes(r.data));
    api.get("/contractor/jobs").then((r) => setJobs(r.data));
  }, [open, item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetched once, only when there's actually a QBO reference to link — a
  // synced transaction's "View in QuickBooks" link needs the environment
  // (sandbox vs production) to pick the right host; the realm itself isn't
  // needed in the URL, QBO resolves that from whichever company the
  // signed-in user currently has active.
  useEffect(() => {
    if (isQboMode && open && qboEnvironment === null) {
      api.get("/contractor/accounting/qbo-environment").then((r) => setQboEnvironment(r.data.environment)).catch(() => {});
    }
  }, [isQboMode, open]); // eslint-disable-line react-hooks/exhaustive-deps

  // The chart of accounts, so a failed sync can be fixed on this one
  // transaction: a different account for a line, or a different "Paid from".
  useEffect(() => {
    if (isQboMode && open) {
      api.get("/contractor/accounting/qbo-accounts").then((r) => setQboAccounts(r.data)).catch(() => {});
    }
  }, [isQboMode, open]);

  if (!item) return null;

  const txn = detail?.transaction;
  const lines = !txn ? []
    : item.type === "purchase_order" ? txn.items
    : item.type === "outside_purchase" || item.type === "invoice" ? txn.lines
    : (txn.assignment?.lines || []);
  const amountOf = (l) => (item.type === "purchase_order" ? l.extended_price : l.amount);
  const status = txn?.accounting_status;
  const actionable = status === "pending_review" || status === "needs_info";
  // Meeting 3: approval happens upstream now, but accounting can still send
  // back or exclude an approved item until it syncs.
  const canSendBack = actionable || status === "ready_for_sync";

  const costCodeLabel = (id) => {
    const cc = costCodes.find((c) => c.id === id);
    return cc ? `${cc.code} — ${cc.name}` : "No cost code";
  };

  // QuickBooks only takes expense lines on expense/COGS-type accounts and
  // pays an Expense from a Credit Card (card) or Bank (cash/check) account.
  const accountById = Object.fromEntries(qboAccounts.map((a) => [a.qbo_id, a]));
  const accountName = (id) => (accountById[id] ? accountById[id].fully_qualified_name || accountById[id].name : id);
  const BAD_LINE_TYPES = ["Accounts Payable", "Accounts Receivable"];
  const PAYMENT_TYPES = { CreditCard: ["Credit Card"], Cash: ["Bank", "Other Current Asset"], Check: ["Bank"] };
  const lineAccounts = qboAccounts.filter((a) => !BAD_LINE_TYPES.includes(a.account_type));
  const costCodeAccountProblem = (cc) => {
    const acct = cc?.qbo_account_ref && accountById[cc.qbo_account_ref];
    return acct && BAD_LINE_TYPES.includes(acct.account_type) ? acct.account_type : null;
  };
  const coding = detail?.qbo;
  const payTypes = PAYMENT_TYPES[coding?.payment_type] || [];
  const paymentAccounts = qboAccounts.filter((a) => payTypes.includes(a.account_type));
  const payKind = coding?.payment_type === "CreditCard" ? "credit card" : "bank";
  const currentPaymentAccount = paymentAccount ?? coding?.payment_account_ref ?? "";
  const contextLabel = (ctx) => {
    if (!ctx || !ctx.type) return "—";
    if (ctx.job_id) {
      const j = jobs.find((j2) => j2.id === ctx.job_id);
      return j ? `${j.code} — ${j.name}` : "Job";
    }
    return "Non-Job";
  };

  const handleError = (e, fallback) => {
    const d = e.response?.data?.detail;
    toast.error(Array.isArray(d) ? d.map((x) => x.message).join(" ") : d || fallback);
  };

  // Excluding is never final — put it back where it was (to sync, or to
  // approve) so it can be synced whenever accounting wants.
  const includeAgain = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/contractor/accounting/${item.type}/${item.id}/restore`);
      toast.success(data.accounting_status === "ready_for_sync" ? "Included again — it's back in To sync" : "Included again — it's back in To approve");
      onOpenChange(false);
      onChanged?.();
    } catch (e) {
      handleError(e, "Failed to include it again");
    } finally {
      setSaving(false);
    }
  };

  const syncToQbo = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/contractor/accounting/${item.type}/${item.id}/sync`);
      if (data.ok) {
        toast.success(`Synced to QuickBooks (${data.qbo_type} ${data.qbo_id})`);
        onOpenChange(false);
        onChanged?.();
      } else {
        toast.error(data.error);
        load();
      }
    } catch (e) {
      handleError(e, "Sync failed");
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    setSaving(true);
    try {
      await api.post(`/contractor/accounting/${item.type}/${item.id}/approve`);
      toast.success("Approved — ready for sync");
      onOpenChange(false);
      onChanged?.();
    } catch (e) {
      handleError(e, "Failed to approve");
    } finally {
      setSaving(false);
    }
  };

  const sendNeedsInfo = async () => {
    if (!needsInfoMessage.trim()) return toast.error("Describe what's missing");
    setSaving(true);
    try {
      await api.post(`/contractor/accounting/${item.type}/${item.id}/needs-info`, { message: needsInfoMessage.trim() });
      toast.success("Marked needs info — requester notified");
      setShowNeedsInfo(false);
      onOpenChange(false);
      onChanged?.();
    } catch (e) {
      handleError(e, "Failed to request info");
    } finally {
      setSaving(false);
    }
  };

  const exclude = async () => {
    if (!excludeReason.trim()) return toast.error("A reason is required");
    setSaving(true);
    try {
      await api.post(`/contractor/accounting/${item.type}/${item.id}/exclude`, { reason: excludeReason.trim() });
      toast.success("Excluded from sync");
      setShowExclude(false);
      onOpenChange(false);
      onChanged?.();
    } catch (e) {
      handleError(e, "Failed to exclude");
    } finally {
      setSaving(false);
    }
  };

  const saveMapping = async () => {
    const edits = Object.entries(lineEdits).map(([index, patch]) => ({ index: Number(index), ...patch }));
    setSaving(true);
    try {
      await api.patch(`/contractor/accounting/${item.type}/${item.id}/mapping`, {
        memo: memo,
        lines: edits,
        ...(paymentAccount !== null ? { qbo_payment_account_ref: paymentAccount } : {}),
      });
      toast.success("Mapping updated");
      setEditingMapping(false);
      setLineEdits({});
      setPaymentAccount(null);
      load();
      onChanged?.();
    } catch (e) {
      handleError(e, "Failed to save mapping");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto p-0" data-testid="accounting-review-drawer">
        {!detail ? (
          <div className="p-6 text-slate-400 text-sm">Loading…</div>
        ) : (
          <div className="p-6">
            <SheetHeader className="text-left">
              <SheetTitle className="flex items-center gap-2 flex-wrap">
                {detail.label} <StatusBadge status={status} />
                {item.needs_credit && <StatusBadge status="received_with_issues" testId="review-needs-credit-flag" />}
              </SheetTitle>
            </SheetHeader>

            {txn.accounting_needs_info_message && status === "needs_info" && (
              <div className="mt-3 text-[12.5px] text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3.5 py-2.5" data-testid="review-needs-info-banner">
                <span className="font-semibold">Waiting on info:</span> {txn.accounting_needs_info_message}
              </div>
            )}
            {txn.accounting_excluded_reason && (
              <div className="mt-3 text-[12.5px] text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3.5 py-2.5">
                <span className="font-semibold">Excluded:</span> {txn.accounting_excluded_reason}
              </div>
            )}
            {txn.sync_error && status === "ready_for_sync" && (
              <div className="mt-3 text-[12.5px] text-red-800 bg-red-50 border border-red-100 rounded-lg px-3.5 py-2.5" data-testid="review-sync-error">
                <span className="font-semibold">Last sync failed:</span> {txn.sync_error.message}
                {!editingMapping && (
                  <button onClick={() => setEditingMapping(true)} data-testid="fix-cost-codes-button" className="mt-2 flex items-center gap-1 font-semibold text-red-700 hover:underline">
                    <Pencil size={12} /> Fix the accounts on this transaction, then sync again
                  </button>
                )}
              </div>
            )}
            {txn.qbo_sync && (
              <div className="mt-3 flex items-center justify-between gap-3 text-[12.5px] text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-3.5 py-2.5" data-testid="review-qbo-synced">
                <span>Synced to QuickBooks as {txn.qbo_sync.qbo_type} #{txn.qbo_sync.qbo_id} · {fmtDateTime(txn.qbo_sync.at)}</span>
                {qboEnvironment && (
                  <a
                    href={qboTransactionLink(qboEnvironment, txn.qbo_sync.qbo_type, txn.qbo_sync.qbo_id)}
                    target="_blank" rel="noopener noreferrer" data-testid="view-in-qbo-link"
                    className="shrink-0 flex items-center gap-1 font-semibold text-[#2CA01C] hover:underline"
                  >
                    <ExternalLink size={12} /> View in QuickBooks
                  </a>
                )}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6 mt-5">
              {/* Left: transaction data */}
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold mb-2">Transaction</div>
                <div className="space-y-2.5 text-[13px]">
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Vendor</span><span className="font-medium text-right">{item.vendor_name}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Date</span><span>{fmtDate(item.date)}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-slate-500">Context</span><span className="text-right">{contextLabel(item.context)}</span></div>
                  {item.type === "outside_purchase" && (
                    <div className="flex justify-between gap-3"><span className="text-slate-500">Payment</span><span>{txn.payment_method ? paymentMethodLabel(txn.payment_method) : "—"}{txn.card_last4 ? ` ···· ${txn.card_last4}` : ""}</span></div>
                  )}
                  {item.type === "card_transaction" && (
                    <div className="flex justify-between gap-3"><span className="text-slate-500">Cardholder</span><span>{txn.cardholder || "—"}{txn.card_last4 ? ` ···· ${txn.card_last4}` : ""}</span></div>
                  )}
                  {isQboMode && coding?.posts_as === "Expense" && (
                    <div className="flex justify-between items-center gap-3" data-testid="review-paid-from">
                      <span className="text-slate-500 shrink-0">Paid from (QuickBooks)</span>
                      {editingMapping ? (
                        <SearchableSelect
                          value={currentPaymentAccount}
                          onChange={(e) => setPaymentAccount(e.target.value)}
                          data-testid="mapping-paid-from"
                          className="h-8 px-2 rounded-lg border border-slate-200 text-[12px] min-w-[200px]"
                        >
                          <option value="">Company default{coding.default_payment_account_ref ? ` (${accountName(coding.default_payment_account_ref)})` : " (not set)"}</option>
                          {paymentAccounts.map((a) => <option key={a.qbo_id} value={a.qbo_id}>{a.fully_qualified_name || a.name}</option>)}
                        </SearchableSelect>
                      ) : (
                        <span className="text-right">
                          {coding.payment_account_ref ? accountName(coding.payment_account_ref)
                            : coding.default_payment_account_ref ? `${accountName(coding.default_payment_account_ref)} (default)`
                            : <span className="text-amber-600 font-medium">No default {payKind} account</span>}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex justify-between gap-3 border-t border-slate-100 pt-2"><span className="text-slate-500 font-semibold">Total</span><span className="mono font-bold">{fmt(item.amount)}</span></div>
                </div>

                <div className="flex items-center justify-between mt-5 mb-2">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">Lines</div>
                  {/* Locked once it's in QuickBooks (Test Drive 2026-10-21). */}
                  {coding?.mapping_locked ? (
                    <span className="text-[11.5px] text-slate-400" data-testid="mapping-locked-note">In QuickBooks — mapping locked</span>
                  ) : !editingMapping && (
                    <button onClick={() => setEditingMapping(true)} data-testid="edit-mapping-button" className="text-[12px] font-semibold text-sky-600 flex items-center gap-1">
                      <Pencil size={12} /> Edit mapping
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {lines.map((l, i) => (
                    <div key={i} className="border border-slate-200 rounded-lg px-3 py-2.5 text-[12.5px]" data-testid={`review-line-${i}`}>
                      <div className="flex justify-between gap-2">
                        <span>{l.description || contextLabel(l.context)}</span>
                        <span className="mono font-medium shrink-0">{fmt(amountOf(l))}</span>
                      </div>
                      {/* Invoice line tax posts to QBO as its own "Sales tax" line on this line's coding (qbo._txn_lines). */}
                      {l.tax_amount > 0 && (
                        <div className="flex justify-between gap-2 text-slate-500 text-[11.5px]">
                          <span>Sales tax{l.tax_rate ? ` (${l.tax_rate}%)` : ""}</span>
                          <span className="mono shrink-0">{fmt(l.tax_amount)}</span>
                        </div>
                      )}
                      {!editingMapping ? (
                        <div className="text-slate-500 mt-1">
                          <div className="flex justify-between gap-2">
                            <span className={!l.cost_code_id ? "text-amber-600 font-medium" : ""}>{costCodeLabel(l.cost_code_id)}</span>
                            <span className="capitalize">{(lineEdits[i]?.billability || l.billability || "").replace("_", "-")}</span>
                          </div>
                          {isQboMode && l.qbo_account_ref && (
                            <div className="text-[11.5px] text-sky-700 mt-0.5" data-testid={`review-line-account-${i}`}>QuickBooks account: {accountName(l.qbo_account_ref)}</div>
                          )}
                          {isQboMode && !l.qbo_account_ref && costCodeAccountProblem(costCodes.find((c) => c.id === l.cost_code_id)) && (
                            <div className="text-[11.5px] text-red-700 mt-0.5">This cost code posts to an {costCodeAccountProblem(costCodes.find((c) => c.id === l.cost_code_id))} account — QuickBooks will reject it. Edit mapping to change it.</div>
                          )}
                        </div>
                      ) : (
                        <div className="mt-2 space-y-2">
                          <div className="flex gap-2">
                          <SearchableSelect
                            value={lineEdits[i]?.cost_code_id ?? l.cost_code_id ?? ""}
                            onChange={(e) => setLineEdits((m) => ({ ...m, [i]: { ...m[i], cost_code_id: e.target.value } }))}
                            data-testid={`mapping-cost-code-${i}`}
                            className="flex-1 h-8 px-2 rounded-lg border border-slate-200 text-[12px]"
                          >
                            <option value="">Cost code…</option>
                            {costCodes.map((c) => (
                              <option key={c.id} value={c.id}>{c.code} — {c.name}{costCodeAccountProblem(c) ? ` (${costCodeAccountProblem(c)} — won't sync)` : ""}</option>
                            ))}
                          </SearchableSelect>
                          <SearchableSelect
                            value={lineEdits[i]?.billability ?? l.billability ?? "non_billable"}
                            onChange={(e) => setLineEdits((m) => ({ ...m, [i]: { ...m[i], billability: e.target.value } }))}
                            data-testid={`mapping-billability-${i}`}
                            className="h-8 px-2 rounded-lg border border-slate-200 text-[12px]"
                          >
                            <option value="non_billable">Non-billable</option>
                            <option value="billable">Billable</option>
                          </SearchableSelect>
                          </div>
                          {isQboMode && (
                            <SearchableSelect
                              value={lineEdits[i]?.qbo_account_ref ?? l.qbo_account_ref ?? ""}
                              onChange={(e) => setLineEdits((m) => ({ ...m, [i]: { ...m[i], qbo_account_ref: e.target.value } }))}
                              data-testid={`mapping-qbo-account-${i}`}
                              className="w-full h-8 px-2 rounded-lg border border-slate-200 text-[12px]"
                            >
                              <option value="">QuickBooks account: from the cost code</option>
                              {lineAccounts.map((a) => <option key={a.qbo_id} value={a.qbo_id}>{a.fully_qualified_name || a.name}{a.account_type ? ` · ${a.account_type}` : ""}</option>)}
                            </SearchableSelect>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-4">
                  <label className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">Accounting Memo</label>
                  {editingMapping ? (
                    <textarea value={memo} onChange={(e) => setMemo(e.target.value)} rows={2} data-testid="mapping-memo-input" className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 text-[12.5px]" placeholder="Internal memo for QBO/export…" />
                  ) : (
                    <div className="mt-1.5 text-[12.5px] text-slate-600">{txn.accounting_memo || <span className="text-slate-400">None</span>}</div>
                  )}
                </div>
                {editingMapping && (
                  <div className="flex gap-2 mt-3">
                    <button disabled={saving} onClick={saveMapping} data-testid="save-mapping-button" className="px-4 h-9 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[12.5px] font-semibold disabled:opacity-60">Save Mapping</button>
                    <button onClick={() => { setEditingMapping(false); setLineEdits({}); setPaymentAccount(null); setMemo(txn.accounting_memo || ""); }} className="px-4 h-9 rounded-lg border border-slate-200 text-[12.5px] font-semibold hover:bg-slate-50">Cancel</button>
                  </div>
                )}
              </div>

              {/* Right: documents */}
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold mb-2">Documents ({detail.documents.length})</div>
                {detail.documents.length === 0 && (
                  <div className="text-[12.5px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3.5 py-3" data-testid="review-no-documents">
                    No documents attached — {item.type === "purchase_order" ? "an invoice" : "a receipt"} is required before approval.
                  </div>
                )}
                <div className="space-y-4">
                  {detail.documents.map((d) => (
                    <div key={d.id} data-testid={`review-document-${d.id}`}>
                      <div className="text-[12px] text-slate-500 mb-1.5 flex items-center gap-2">
                        <span className="ss-pill ss-pill-slate">{d.doc_type.replace(/_/g, " ")}</span>
                        <span className="truncate">{d.filename}</span>
                      </div>
                      <DocPreview doc={d} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {status === "excluded" && (
              <div className="mt-6 pt-4 border-t border-slate-100" data-testid="excluded-panel">
                <div className="text-[12.5px] text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3.5 py-3">
                  <div className="font-semibold text-[11px] uppercase tracking-wide text-slate-400 mb-0.5">Excluded from sync</div>
                  {txn?.accounting_excluded_reason || "No reason recorded."}
                </div>
                <button disabled={saving} onClick={includeAgain} data-testid="include-again-button" className="mt-3 w-full h-11 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50 disabled:opacity-60 flex items-center justify-center gap-1.5">
                  <RotateCcw size={15} /> {saving ? "Including…" : "Include again"}
                </button>
              </div>
            )}

            {status === "ready_for_sync" && isQboMode && (
              <div className="mt-6 pt-4 border-t border-slate-100">
                <button disabled={saving} onClick={syncToQbo} data-testid="sync-to-qbo-button" className="w-full h-11 rounded-lg bg-[#2CA01C] hover:bg-[#248217] text-white text-[13px] font-semibold disabled:opacity-60 flex items-center justify-center gap-1.5">
                  <UploadCloud size={15} /> {saving ? "Syncing…" : "Sync to QuickBooks"}
                </button>
              </div>
            )}

            {canSendBack && (
              <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-slate-100">
                {actionable && (
                  <button disabled={saving} onClick={approve} data-testid="approve-transaction-button" className="flex-1 min-w-[160px] h-11 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-[13px] font-semibold disabled:opacity-60 flex items-center justify-center gap-1.5">
                    <CheckCircle2 size={15} /> Approve
                  </button>
                )}
                <button onClick={() => setShowNeedsInfo(true)} data-testid="needs-info-button" className="h-11 px-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-[13px] font-semibold hover:bg-amber-100 flex items-center gap-1.5">
                  <MessageCircleQuestion size={15} /> {item?.type === "outside_purchase" ? "Send Back" : "Needs Info"}
                </button>
                <button onClick={() => setShowExclude(true)} data-testid="exclude-transaction-button" className="h-11 px-4 rounded-lg border border-slate-200 text-slate-600 text-[13px] font-semibold hover:bg-slate-50 flex items-center gap-1.5">
                  <Ban size={15} /> Exclude
                </button>
              </div>
            )}
          </div>
        )}

        <Dialog open={showNeedsInfo} onOpenChange={setShowNeedsInfo}>
          <DialogContent data-testid="needs-info-dialog">
            <DialogHeader><DialogTitle>Request More Information</DialogTitle></DialogHeader>
            <p className="text-[12.5px] text-slate-500">The requester gets a bell notification and an email with your message.</p>
            <textarea
              value={needsInfoMessage} onChange={(e) => setNeedsInfoMessage(e.target.value)} rows={3}
              placeholder="e.g. Missing itemized receipt — please attach one" data-testid="needs-info-message-input"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px]"
            />
            <button disabled={saving} onClick={sendNeedsInfo} data-testid="confirm-needs-info-button" className="w-full h-11 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-semibold disabled:opacity-60">
              {saving ? "Sending…" : "Send Request"}
            </button>
          </DialogContent>
        </Dialog>

        <Dialog open={showExclude} onOpenChange={setShowExclude}>
          <DialogContent data-testid="exclude-dialog">
            <DialogHeader><DialogTitle>Exclude from Sync</DialogTitle></DialogHeader>
            <p className="text-[12.5px] text-slate-500">This transaction will never sync to accounting. The reason is audit-logged.</p>
            <textarea
              value={excludeReason} onChange={(e) => setExcludeReason(e.target.value)} rows={3}
              placeholder="Reason for exclusion (required)" data-testid="exclude-reason-input"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px]"
            />
            <button disabled={saving} onClick={exclude} data-testid="confirm-exclude-transaction-button" className="w-full h-11 rounded-lg bg-red-500 hover:bg-red-600 text-white text-[13px] font-semibold disabled:opacity-60">
              {saving ? "Excluding…" : "Confirm Exclusion"}
            </button>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}
